import { readableJoinStringArray } from '@anchorage/utils';
import { Button } from '@latitude/button';
import { Icon } from '@latitude/icons';
import { Text } from '@latitude/text';
import { cn } from '@latitude/token-utils';
import React, { useId, useMemo, useRef, useState } from 'react';

import { FileList } from './components/FileList';
import { DEFAULT_FILE_MESSAGE } from './constants';
import type { FileUploadProps, UploadedFile } from './types';
import {
  acceptToLabels,
  fileMatchesAccept,
  generateFileMessage,
} from './utils';
import { fileUploadLabelVariants } from './variants/file-upload-label-variants';

export const FileUpload = ({
  id,
  externalFiles,
  onChange,
  multiple,
  disabled,
  error,
  maxSizeMB,
  showMaxSizeText,
  maxSizeText,
  directUploadToApi = false,
  onClear,
  classNames = {},
  ref,
  'aria-label': ariaLabel,
  rawProps,
  ...restProps
}: FileUploadProps) => {
  const inputGenId = useId();
  const inputId = id ?? inputGenId;
  const defaultRef = useRef<HTMLInputElement>(null);
  const fileInputRef = ref || defaultRef;

  const [internalFiles, setInternalFiles] = useState<UploadedFile[]>([]);
  const [dragging, setDragging] = useState(false);

  // if we don't want to use an external state, we can use the internalFiles
  const fileList = externalFiles || internalFiles;

  // Generate dynamic file type message based on accept prop
  const helperText = useMemo(() => {
    const acceptValue = rawProps?.accept;
    if (!acceptValue || typeof acceptValue !== 'string') {
      return DEFAULT_FILE_MESSAGE;
    }

    try {
      const labels = acceptToLabels(acceptValue);
      return generateFileMessage(labels);
    } catch {
      return DEFAULT_FILE_MESSAGE;
    }
  }, [rawProps?.accept]);

  const maxSizeCaption = useMemo(() => {
    if (!showMaxSizeText) {
      return null;
    }
    if (maxSizeText) {
      return maxSizeText;
    }
    if (maxSizeMB) {
      return `Maximum file size is ${maxSizeMB}MB`;
    }
    return null;
  }, [showMaxSizeText, maxSizeText, maxSizeMB]);

  const storeInternalFiles = (files: File[]) => {
    const accept = rawProps?.accept;
    const newFilesWithPercentage = files.map((file) => {
      const hasExtensionError =
        typeof accept === 'string' && !fileMatchesAccept(file, accept);
      const fileSize = file.size / (1024 * 1024);
      const hasSizeError = !!maxSizeMB && fileSize > maxSizeMB;
      const hasError = hasExtensionError || hasSizeError;
      const initialProgressValue = directUploadToApi ? 0 : 100;

      const errorMessage = hasExtensionError
        ? `Invalid file type. Please upload a ${readableJoinStringArray(acceptToLabels(accept), 'or')} file.`
        : hasSizeError
          ? `It seems your file is too large. Please upload a file that is ${maxSizeMB} MB or smaller.`
          : undefined;

      return {
        file: hasError ? null : file,
        name: file.name,
        errorMessage,
        // Show 100% progress for successfully loaded files (local file reading is instant)
        // Show undefined progress for files with errors (no progress bar will show)
        progress: hasError ? undefined : initialProgressValue,
      };
    });
    const newFiles = multiple
      ? [...internalFiles, ...newFilesWithPercentage]
      : newFilesWithPercentage;
    setInternalFiles(newFiles);

    return newFiles;
  };

  // Handle file selection
  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files?.length) {
      const selectedFiles = Array.from(event.target.files);
      const newInternalFiles = storeInternalFiles(selectedFiles);

      if (newInternalFiles && onChange) {
        onChange(newInternalFiles);
      }
    }
  };

  const handleClear =
    (index: number) => (event: React.MouseEvent<HTMLButtonElement>) => {
      if (fileInputRef?.current?.value) {
        fileInputRef.current.value = '';
      }
      const remainingFiles = fileList.filter((_, i) => i !== index);
      onChange?.(remainingFiles);
      setInternalFiles(remainingFiles);
      onClear?.(event, index);
    };

  // Handle keyboard and drag events
  const handleKeyDown = (e: React.KeyboardEvent<HTMLLabelElement>) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      fileInputRef?.current?.click();
    }
  };
  const handleDragOver = (event: React.DragEvent<HTMLLabelElement>) => {
    event.preventDefault();
    setDragging(true);
  };
  const handleDragLeave = () => {
    setDragging(false);
  };
  const handleDrop = (event: React.DragEvent<HTMLLabelElement>) => {
    event.preventDefault();
    setDragging(false);

    handleFileChange({
      target: { files: event.dataTransfer.files },
    } as React.ChangeEvent<HTMLInputElement>);
  };

  return (
    <div id="wrapper">
      <label
        htmlFor={inputId}
        tabIndex={0}
        onKeyDown={handleKeyDown}
        className={cn(
          fileUploadLabelVariants({
            isActive: dragging,
            hasError: error,
          }),
          classNames.label,
        )}
        aria-label={ariaLabel}
        aria-disabled={disabled}
        onDragOver={!disabled ? handleDragOver : undefined}
        onDragLeave={!disabled ? handleDragLeave : undefined}
        onDrop={!disabled ? handleDrop : undefined}
      >
        <Icon name="upload" width="40px" height="40px" />
        <Text variant="bodyMedium">
          {helperText}
          {maxSizeCaption && (
            <Text
              variant="captionMedium"
              color={disabled ? 'disabled' : 'muted'}
              className="mt-spacing-ref-xtiny"
            >
              {maxSizeCaption}
            </Text>
          )}
        </Text>
        <Button
          asChild
          variant="neutral"
          size="tiny"
          type="filled"
          className="w-fit"
          disabled={disabled}
        >
          Browse for files
        </Button>
        <input
          type="file"
          ref={fileInputRef}
          id={inputId}
          onChange={handleFileChange}
          className="block h-0 opacity-0"
          disabled={disabled}
          multiple={multiple}
          aria-required={restProps.required}
          aria-invalid={error}
          aria-disabled={disabled}
          {...restProps}
          {...rawProps}
        />
      </label>
      <FileList files={fileList} onClear={disabled ? undefined : handleClear} />
    </div>
  );
};
